import React, { createContext, useContext, useState } from 'react';
import { User } from '../types/user.interface';
import { apiService } from '../services/api';
import { useAuthContext } from './AuthContext';
import { isScopedAccessToken } from '../lib/auth/tokenClaims';

interface UserContextType {
  user: User | null;
  loading: boolean;
  refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

function buildUserFromToken(token: string): User {
  const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  const businessId = payload.businessId != null ? parseInt(String(payload.businessId), 10) : null;
  const name =
    payload.name ??
    payload.fullName ??
    payload.given_name ??
    payload.preferred_username ??
    'User';
  return {
    userId: typeof payload.sub === 'number' ? payload.sub : 0,
    fullName: typeof name === 'string' ? name : 'User',
    email: payload.email ?? '',
    phoneNumber: null,
    countryCode: null,
    userType: payload.userType ?? 4,
    isVerified: true,
    createdAt: '',
    businessId: Number.isNaN(businessId) ? null : businessId,
  };
}

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const { accessToken } = useAuthContext();

  const refreshUser = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setUser(null);
        return;
      }
      try {
        const userData = await apiService.getCurrentUser();
        setUser({
          userId: userData.userId ?? (userData as unknown as { sub?: number }).sub ?? 0,
          fullName: userData.fullName ?? 'User',
          email: userData.email ?? '',
          phoneNumber: userData.phoneNumber ?? null,
          countryCode: userData.countryCode ?? null,
          userType: userData.userType ?? 4,
          isVerified: userData.isVerified ?? true,
          createdAt: userData.createdAt ?? '',
          businessId: userData.businessId ?? null,
        });
      } catch {
        setUser(buildUserFromToken(token));
      }
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!accessToken) {
      setUser(null);
      return;
    }
    // AB#1434.b — Auth v2 identity-only tokens (post-login chooser-pending
    // state) carry no userProfileId/businessId claims, so `/auth/me`
    // returns 401. Defer refreshUser until `switchProfileV2` mints a
    // scoped token. Keeping `user=null` is safe: every caller of
    // `useUser` tolerates null until the profile is picked.
    if (!isScopedAccessToken(accessToken)) {
      setUser(null);
      return;
    }
    refreshUser();
  }, [accessToken]);

  return (
    <UserContext.Provider value={{ user, loading, refreshUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
